import { AnyAction } from 'redux';
import {
  GlobalActions,
  VersicherungsnehmerCommonInsuranceData,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  determineAssekuranztarifFromTarifbereich,
  TierartEnum,
  TkvVorbelegungdaten,
  ValueLabelElement,
} from '@ps20-frontend/frontend-models';
import {
  DiagnoseVorvertrag,
  EinsatzgebieteTier,
  KennzeichnungsArt,
  Tarifbereich,
  Tierart,
  Tiergeschlecht,
  Tierhaltung,
} from '@ps20/bff-models';
import cloneDeep from 'lodash/cloneDeep';
import { convertDateFormatFromShortIsoToLocalState } from '@ps20-frontend/utils';
import { TkvActions } from '../tkvActions';
import { IAppState } from '../appState';

export enum GeschlechtEnum {
  MAENNLICH = 'MÄNNLICH',
  WEIBLICH = 'WEIBLICH',
}

export enum BehandlungEnum {
  MIT_OPERATION = 'MIT_OPERATION',
  OHNE_OPERATION = 'OHNE_OPERATION',
  OHNE_TA_BESUCH = 'OHNE_TA_BESUCH',
}

export enum KatzenhaltungEnum {
  WOHNUNGSKATZE = 'WOHNUNGSKATZE',
  FREIGAENGER = 'FREIGAENGER',
}

export interface Behandlungen {
  mitOperation: boolean;
  ohneOperation: boolean;
  ohneTABesuch: boolean;
}

export interface Versicherungsnehmer extends VersicherungsnehmerCommonInsuranceData {
  tarifbereich?: Tarifbereich;
  tierart: TierartEnum;
  geschlecht: GeschlechtEnum;
  rasse: ValueLabelElement;
  hunderassenAuswahlElemente: Array<ValueLabelElement>;
  pferderassenAuswahlElemente: Array<ValueLabelElement>;
  katzenrassenAuswahlElemente: Array<ValueLabelElement>;
  geburtsdatumTier: string;
  isTierKastriert: boolean;
  isBehandelt: boolean;
  behandlungen: Behandlungen;
  anzahlOperationen: number;
  hasErkrankungen: boolean;
  katzenhaltung: KatzenhaltungEnum;
  tiername?: string;
  einsatzgebiet?: EinsatzgebieteTier;
  kennzeichnungsArt?: KennzeichnungsArt;
  kennzeichen?: string;
  diagnose?: DiagnoseVorvertrag[];
}

export const versicherungsnehmerReducer = function (
  sectionState: Versicherungsnehmer = null,
  action: AnyAction,
  state?: IAppState,
): Versicherungsnehmer {
  switch (action.type) {
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA_AFTER_VV_LOAD:
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA: {
      return {
        ...sectionState,
        isAssekuranztarif: action.payload,
      };
    }
    case GlobalActions.TARIFBEREICH_CHANGED: {
      return {
        ...sectionState,
        tarifbereich: action.payload,
        isAssekuranztarif: determineAssekuranztarifFromTarifbereich(action.payload),
      };
    }
    case TkvActions.GEBURTSDATUM_CHANGED: {
      const geburtsdatum: string = action.payload;
      return { ...sectionState, geburtsdatum };
    }
    case TkvActions.TKV_TIERART_CHANGED: {
      const tierart: TierartEnum = action.payload;
      const rasse: ValueLabelElement = null;
      const katzenhaltung: KatzenhaltungEnum = null;
      return { ...sectionState, tierart, rasse, katzenhaltung };
    }
    case TkvActions.GESCHLECHT_CHANGED: {
      const geschlecht: GeschlechtEnum = action.payload;
      return { ...sectionState, geschlecht };
    }
    case TkvActions.RASSE_CHANGED: {
      const rasse: ValueLabelElement = action.payload;
      return { ...sectionState, rasse };
    }
    case GlobalActions.HUNDERASSENLISTE_RETRIEVED: {
      const hunderassenAuswahlElemente: Array<ValueLabelElement> = action.payload;
      return { ...sectionState, hunderassenAuswahlElemente };
    }
    case GlobalActions.PFERDERASSEN_LISTE_RETRIEVED: {
      const pferderassenAuswahlElemente: Array<ValueLabelElement> = action.payload;
      return { ...sectionState, pferderassenAuswahlElemente };
    }
    case GlobalActions.KATZENRASSEN_LISTE_RETRIEVED: {
      const katzenrassenAuswahlElemente: Array<ValueLabelElement> = action.payload;
      return { ...sectionState, katzenrassenAuswahlElemente };
    }
    case TkvActions.GEBURTSDATUM_TIER_CHANGED: {
      const geburtsdatumTier: string = action.payload;
      return { ...sectionState, geburtsdatumTier };
    }
    case TkvActions.TIER_KASTRIERT_STERILISIERT_CHANGED: {
      const isTierKastriert: boolean = action.payload;
      return { ...sectionState, isTierKastriert };
    }
    case TkvActions.IN_BEHANDLUNG_CHANGED: {
      const newState: Versicherungsnehmer = cloneDeep(sectionState);
      const behandlungen = newState.behandlungen;
      const isBehandelt: boolean = action.payload;
      let anzahlOperationen = sectionState.anzahlOperationen;
      if (!isBehandelt) {
        behandlungen.ohneOperation = false;
        behandlungen.mitOperation = false;
        anzahlOperationen = 0;
      }
      return { ...sectionState, behandlungen, isBehandelt, anzahlOperationen };
    }
    case TkvActions.BEHANDLUNGEN_CHANGED: {
      return behandlungenChanged(sectionState, action.payload);
    }
    case TkvActions.ANZAHL_OPERATIONEN_CHANGED: {
      const anzahlOperationen: number = action.payload;
      return { ...sectionState, anzahlOperationen: anzahlOperationen };
    }
    case TkvActions.ERKRANKUNGEN_CHANGED: {
      const hasErkrankungen: boolean = action.payload;
      return { ...sectionState, hasErkrankungen: hasErkrankungen };
    }
    case TkvActions.KATZENHALTUNG_CHANGED: {
      const katzenhaltung: KatzenhaltungEnum = action.payload;
      return { ...sectionState, katzenhaltung: katzenhaltung };
    }
    case TkvActions.VORBELEGUNG_FOR_TKV_ERSATZ_REQUESTED:
      return vorbelegungForTkvErsatz(sectionState, action.payload, state);
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED:
      return vorbelegungForBeibehaltung(sectionState, action.payload, state);
    default:
      return sectionState;
  }
};

function vorbelegungForTkvErsatz(
  sectionState: Versicherungsnehmer,
  actionPayload: TkvVorbelegungdaten,
  state: IAppState,
): Versicherungsnehmer {
  if (actionPayload.forApp === App.TKV && actionPayload.angebotsId === state.angebotsId) {
    const tierart: TierartEnum = convertFromBffTierart(actionPayload.tierart);
    return {
      ...sectionState,
      katzenhaltung: convertFromBffTierhaltung(actionPayload.tierhaltung),
      tierart,
      geschlecht: actionPayload.tiergeschlecht
        ? convertFromBffTiergeschlecht(actionPayload.tiergeschlecht)
        : GeschlechtEnum.MAENNLICH,
      isTierKastriert: actionPayload.isKastriert,
      rasse: {
        value: actionPayload.tierrasse,
        label: getRasseLabel(tierart, state, actionPayload.tierrasse),
      },
      geburtsdatumTier: convertDateFormatFromShortIsoToLocalState(
        actionPayload.geburtsdatumTier,
      ),
      tiername: actionPayload.tiername,
      kennzeichnungsArt: actionPayload.kennzeichnungsArt,
      einsatzgebiet: actionPayload.einsatzgebiet,
      kennzeichen: actionPayload.kennzeichen,
      diagnose: actionPayload.diagnose,
    };
  }
  return sectionState;
}

function vorbelegungForBeibehaltung(
  sectionState: Versicherungsnehmer,
  actionPayload: TkvVorbelegungdaten,
  state: IAppState,
): Versicherungsnehmer {
  const hasOriginalBreed = 'originalBreed' in actionPayload && actionPayload.originalBreed != null;
  const tierrasseValue = hasOriginalBreed
    ? (actionPayload as TkvVorbelegungdaten & { originalBreed: string }).originalBreed
    : actionPayload.tierrasse;

  if (actionPayload.forApp === App.TKV && actionPayload.angebotsId === state.angebotsId) {
    return {
      ...sectionState,
      katzenhaltung: convertFromBffTierhaltung(actionPayload.tierhaltung),
      tierart: convertFromBffTierart(actionPayload.tierart),
      geschlecht: convertFromBffTiergeschlecht(actionPayload.tiergeschlecht),
      isTierKastriert: actionPayload.isKastriert,
      hasErkrankungen: actionPayload.hasHeilbehandlungen,
      anzahlOperationen: actionPayload.anzVorschaedenOp,
      rasse: { value: tierrasseValue, label: null },
      geburtsdatumTier: convertDateFormatFromShortIsoToLocalState(
        actionPayload.geburtsdatumTier,
      ),
    };
  }
  return sectionState;
}

function behandlungenChanged(
  sectionState: Versicherungsnehmer,
  behandlungen: BehandlungEnum[],
): Versicherungsnehmer {
  const newState: Versicherungsnehmer = cloneDeep(sectionState);
  const newBehandlungen = newState.behandlungen;
  let anzahlOperationen = newState.anzahlOperationen;
  newBehandlungen.ohneTABesuch = !!behandlungen?.includes(BehandlungEnum.OHNE_TA_BESUCH);
  if (behandlungen?.includes(BehandlungEnum.MIT_OPERATION)) {
    newBehandlungen.mitOperation = true;
    anzahlOperationen = anzahlOperationen > 1 ? anzahlOperationen : 1;
  } else {
    newBehandlungen.mitOperation = false;
    anzahlOperationen = 0;
  }
  newBehandlungen.ohneOperation = behandlungen?.includes(BehandlungEnum.OHNE_OPERATION);
  return { ...newState, behandlungen: newBehandlungen, anzahlOperationen };
}

export function convertFromBffTierhaltung(katzenhaltung: Tierhaltung): KatzenhaltungEnum {
  switch (katzenhaltung) {
    case Tierhaltung.WOHNUNGSKATZE:
      return KatzenhaltungEnum.WOHNUNGSKATZE;
    case Tierhaltung.FREIGAENGER:
      return KatzenhaltungEnum.FREIGAENGER;
    default:
      return null;
  }
}

export function convertFromBffTierart(tierart: Tierart): TierartEnum {
  switch (tierart) {
    case Tierart.VRHUND:
      return TierartEnum.HUND;
    case Tierart.VRKATZE:
      return TierartEnum.KATZE;
    case Tierart.VRPFERD:
      return TierartEnum.PFERD;
    default:
      return null;
  }
}

export function convertFromBffTiergeschlecht(geschlecht: Tiergeschlecht): GeschlechtEnum {
  switch (geschlecht) {
    case Tiergeschlecht.MAENNLICH:
      return GeschlechtEnum.MAENNLICH;
    case Tiergeschlecht.WEIBLICH:
      return GeschlechtEnum.WEIBLICH;
    default:
      return null;
  }
}

export function getRasseLabel(tierart: TierartEnum, state: IAppState, rasseWert: string): string {
  switch (tierart) {
    case TierartEnum.KATZE:
      return state.versicherungsnehmer.katzenrassenAuswahlElemente?.find(
        rassen => rassen.value === rasseWert,
      )?.label;
    case TierartEnum.PFERD:
      return state.versicherungsnehmer.pferderassenAuswahlElemente?.find(
        rassen => rassen.value === rasseWert,
      )?.label;
    case TierartEnum.HUND:
      return state.versicherungsnehmer.hunderassenAuswahlElemente?.find(
        rassen => rassen.value === rasseWert,
      )?.label;
    default:
      return null;
  }
}
